import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Download, X, Smartphone, Apple, Share2 } from 'lucide-react';

const DISMISS_KEY = 'pf_install_dismissed_at';
const DISMISS_HOURS = 24 * 7; // re-show after 7 days

const isIos = () => /iphone|ipad|ipod/.test((navigator.userAgent || '').toLowerCase());
const isInStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  // iOS Safari sets navigator.standalone
  // eslint-disable-next-line no-undef
  window.navigator.standalone === true;

const wasDismissedRecently = () => {
  const at = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
  if (!at) return false;
  return (Date.now() - at) < DISMISS_HOURS * 60 * 60 * 1000;
};

export default function InstallPrompt() {
  const [installEvent, setInstallEvent] = useState(null); // Android Chrome BeforeInstallPromptEvent
  const [showIosTips, setShowIosTips] = useState(false);
  const [dismissed, setDismissed] = useState(wasDismissedRecently() || isInStandalone());

  useEffect(() => {
    if (isInStandalone()) return;

    // Android Chrome / Edge / Brave fire this event when install is possible
    const handler = (e) => {
      e.preventDefault();
      setInstallEvent(e);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS Safari doesn't fire beforeinstallprompt — show tips manually after a delay
    if (isIos() && !wasDismissedRecently()) {
      const t = setTimeout(() => setShowIosTips(true), 3000);
      return () => {
        window.removeEventListener('beforeinstallprompt', handler);
        clearTimeout(t);
      };
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
    setShowIosTips(false);
    setInstallEvent(null);
  };

  const triggerAndroidInstall = async () => {
    if (!installEvent) return;
    installEvent.prompt();
    const { outcome } = await installEvent.userChoice;
    if (outcome === 'accepted') {
      setInstallEvent(null);
      setDismissed(true);
    }
  };

  if (dismissed) return null;

  // Android: native prompt
  if (installEvent) {
    return (
      <div
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:w-96 z-[60]"
        data-testid="install-prompt-android"
      >
        <Card className="p-4 glass-card border-primary/40 shadow-2xl">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0">
              <Smartphone className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold mb-1">Install PeptideForge</p>
              <p className="text-xs text-muted-foreground mb-3">
                Add to your home screen for full-screen, offline-capable access.
              </p>
              <div className="flex gap-2">
                <Button size="sm" onClick={triggerAndroidInstall} data-testid="install-android-button">
                  <Download className="h-3.5 w-3.5 mr-1.5" /> Install
                </Button>
                <Button size="sm" variant="ghost" onClick={dismiss} data-testid="install-dismiss-android">
                  Not now
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // iOS: instructional card
  if (showIosTips) {
    return (
      <div
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:w-96 z-[60]"
        data-testid="install-prompt-ios"
      >
        <Card className="p-4 glass-card border-primary/40 shadow-2xl">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="flex items-center gap-2">
              <Apple className="h-5 w-5 text-primary" />
              <p className="font-semibold">Install on iPhone</p>
            </div>
            <button onClick={dismiss} aria-label="Dismiss" className="text-muted-foreground hover:text-foreground" data-testid="install-dismiss-ios">
              <X className="h-4 w-4" />
            </button>
          </div>
          <ol className="text-xs text-muted-foreground space-y-1.5 list-decimal ml-4">
            <li>Tap the <Share2 className="inline h-3 w-3 mx-0.5" /> <strong>Share</strong> button in Safari's toolbar.</li>
            <li>Scroll down and tap <strong>"Add to Home Screen"</strong>.</li>
            <li>Tap <strong>Add</strong> in the top right — done!</li>
          </ol>
        </Card>
      </div>
    );
  }

  return null;
}
